import React from "react";

interface UpgradePromptProps {
  featureName: string;
  onUpgrade?: () => void;
  className?: string;
}

/**
 * Reusable component to prompt users to upgrade for locked features
 */
export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  featureName,
  onUpgrade,
  className = "",
}) => {
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Default: navigate to profile/billing page
      window.location.href = "/profile#billing";
    }
  };

  return (
    <div
      className={`bg-purple-900 bg-opacity-20 border border-purple-500 rounded-lg p-4 ${className}`}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-purple-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-purple-300 mb-1">
            Pro Feature
          </h4>
          <p className="text-sm text-purple-200 mb-3">
            {featureName} is only available on the Pro plan. Upgrade to unlock
            this feature and more.
          </p>
          <button
            onClick={handleUpgrade}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
          >
            ✨ Upgrade to Pro
          </button>
        </div>
      </div>
    </div>
  );
};

interface ProBadgeProps {
  className?: string;
}

/**
 * Small PRO badge to mark premium features
 */
export const ProBadge: React.FC<ProBadgeProps> = ({ className = "" }) => {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-white ${className}`}
    >
      PRO
    </span>
  );
};

/**
 * Tooltip component for upgrade prompts
 */
interface UpgradeTooltipProps {
  featureName: string;
  children: React.ReactNode;
}

export const UpgradeTooltip: React.FC<UpgradeTooltipProps> = ({
  featureName,
  children,
}) => {
  const [show, setShow] = React.useState(false);

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 z-50">
          <div className="bg-gray-900 border border-purple-500 rounded-lg p-3 shadow-xl">
            <div className="text-xs text-purple-300 mb-1 font-medium">
              Pro Feature
            </div>
            <div className="text-xs text-gray-300">
              {featureName} requires a Pro plan subscription.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

